import { usePathname, useRouter } from 'next/navigation';
import { useAppDispatch } from '@/hooks/redux';
import { authService } from '@/features/auth/services/authService';
import { loginWithVerificationCustomToken } from '@/store/slices/authSlice';
import { emit } from '@/utils/emitter';
import { usePlan } from '@/features/subscription/hooks/usePlan';
import {
  VerificationCodeReason,
  VerificationResendData,
  VerificationSendData,
} from '@/features/auth/types/http/verificationCode';

interface VerificationErrorPayload {
  message?: string;
  reason?: VerificationCodeReason;
  retryAfterSeconds?: number;
  data?: VerificationErrorPayload;
}

export class VerificationFlowError extends Error {
  reason?: VerificationCodeReason;
  retryAfterSeconds?: number;

  constructor(payload: VerificationErrorPayload = {}) {
    const reason = payload.reason;
    const messages: Record<string, string> = {
      INVALID_CODE: 'Invalid verification code. Please try again',
      CODE_EXPIRED: 'This code has expired. Request a new code.',
      RATE_LIMIT_EXCEEDED:
        'Too many requests. Please wait before requesting another code.',
      EMAIL_ALREADY_VERIFIED:
        'A verification code could not be sent. Log in with your password instead.',
    };

    super(
      (reason && messages[reason]) ||
        payload.message ||
        'Something went wrong. Please try again.'
    );
    this.name = 'VerificationFlowError';
    this.reason = reason;
    this.retryAfterSeconds = payload.retryAfterSeconds;
  }
}

const normalizeVerificationError = (error: unknown) => {
  if (error instanceof VerificationFlowError) return error;
  if (typeof error === 'string') {
    return new VerificationFlowError({ message: error });
  }

  const apiError = error as {
    message?: string;
    data?: VerificationErrorPayload;
  };
  const payload = apiError?.data?.data ?? apiError?.data ?? {};

  return new VerificationFlowError({
    ...payload,
    message: payload.message ?? apiError?.message,
  });
};

const assertSuccessfulResponse = (success: boolean, data: unknown) => {
  if (!success) {
    throw new VerificationFlowError(data as VerificationErrorPayload);
  }
};

const useVerificationCodeLogin = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { refreshData } = usePlan();

  const checkEmail = async (email: string) => {
    try {
      const response = await authService.checkVerificationEmail(email);
      assertSuccessfulResponse(response.data.success, response.data.data);
      return response.data.data;
    } catch (error) {
      throw normalizeVerificationError(error);
    }
  };

  const sendCode = async (email: string): Promise<VerificationSendData> => {
    try {
      const response = await authService.sendVerificationCode(email);
      const data = response.data.data;
      assertSuccessfulResponse(response.data.success && data.codeSent, data);
      return data;
    } catch (error) {
      throw normalizeVerificationError(error);
    }
  };

  const sendLoginCode = async (
    email: string
  ): Promise<VerificationSendData> => {
    try {
      const response = await authService.sendVerificationLoginCode(email);
      const data = response.data.data;
      assertSuccessfulResponse(response.data.success && data.codeSent, data);
      return data;
    } catch (error) {
      throw normalizeVerificationError(error);
    }
  };

  const resendCode = async (email: string): Promise<VerificationResendData> => {
    try {
      const response = await authService.resendVerificationCode(email);
      const data = response.data.data;
      assertSuccessfulResponse(
        response.data.success && data.success && data.codeSent,
        data
      );
      return data;
    } catch (error) {
      throw normalizeVerificationError(error);
    }
  };

  const verifyAndLogin = async (email: string, code: string) => {
    try {
      const response = await authService.verifyCode(email, code);
      const data = response.data.data;
      const customToken = data.firebaseCustomToken ?? data.customToken;

      assertSuccessfulResponse(
        response.data.success &&
          data.success !== false &&
          data.verified !== false,
        data
      );

      if (!customToken) {
        throw new VerificationFlowError({
          message:
            'Verification succeeded, but sign-in could not be completed.',
        });
      }

      const userData = await dispatch(
        loginWithVerificationCustomToken(customToken)
      ).unwrap();

      emit('auth:login-register', userData.user);
      await refreshData({ withUserRefresh: false });

      if (pathname === '/') router.push('/app');

      return userData.user;
    } catch (error) {
      throw normalizeVerificationError(error);
    }
  };

  return { checkEmail, sendCode, sendLoginCode, resendCode, verifyAndLogin };
};

export default useVerificationCodeLogin;
